import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, message, Popconfirm, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import { useDeleteVendor, useGetVendors } from "~/services/vendor";
import { TVendor } from "~/types";
import { VendorModal } from "../modals/vendor-modal";

interface VendorsTabProps {}

export const VendorsTab: React.FC<VendorsTabProps> = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<TVendor | null>(null);

  const { data: vendorsData, isLoading, refetch } = useGetVendors();
  const deleteVendor = useDeleteVendor();

  const vendors = vendorsData?.items || [];

  const handleEdit = (vendor: TVendor) => {
    setEditingVendor(vendor);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingVendor(null);
    setModalOpen(true);
  };

  const handleDelete = async (vendorId: string) => {
    try {
      await deleteVendor.mutateAsync(vendorId);
      message.success("Vendor deleted successfully");
      refetch();
    } catch {
      message.error("Failed to delete vendor");
    }
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const columns: ColumnsType<TVendor> = [
    {
      title: "Vendor ID",
      dataIndex: "vendorRef",
      key: "vendorRef",
      width: "10%",
    },
    {
      title: "Vendor Name",
      dataIndex: "name",
      key: "name",
      width: "15%",
    },
    {
      title: "Services",
      dataIndex: "services",
      key: "services",
      width: "20%",
      render: (services: string[]) => (
        <Space size={[0, 4]} wrap>
          {services?.map((service, index) => (
            <Tag key={index} color="blue">
              {service}
            </Tag>
          )) || "-"}
        </Space>
      ),
    },
    {
      title: "Contact",
      dataIndex: "contact",
      key: "contact",
      render: (contact) => contact || "-",
      width: "12%",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "-",
      width: "18%",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
      width: "12%",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: "13%",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            // onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Delete Vendor"
            description="Are you sure you want to delete this vendor?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium mb-2">3PL Vendors</h3>
          <p className="text-gray-500">
            Manage third-party logistics vendors and their services
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add New Vendor
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={vendors}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} vendors`,
        }}
        scroll={{ x: 1200 }}
        className="bg-white"
      />

      <VendorModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        vendor={editingVendor}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
